/**
 * `expo-asset` no browser.
 *
 * No RN, `require("../assets/x.onnx")` dá um número de módulo e o `Asset` resolve o arquivo.
 * No Vite o import do asset já É a URL (`import url from "@/assets/models/x.onnx?url"`), então
 * `Asset.fromModule(url)` só embrulha essa URL. `downloadAsync()` faz um `fetch` (aquece o
 * cache HTTP/service worker e confirma que existe) e preenche `localUri` com a própria URL —
 * `FileSystem.copyAsync` aceita URL http(s), então o código portado literalmente funciona.
 */
import { useEffect, useState } from "react";

export type AssetMetadata = {
  uri: string;
  name?: string;
  type?: string;
  hash?: string | null;
  width?: number;
  height?: number;
};

const cachePorUri = new Map<string, Asset>();

function nomeEtipo(uri: string): { name: string; type: string } {
  const semQuery = uri.split(/[?#]/)[0];
  const arquivo = decodeURIComponent(semQuery.split("/").pop() || "asset");
  const ponto = arquivo.lastIndexOf(".");
  // Vite acrescenta hash: "modelo-AbC123.onnx" → nome "modelo-AbC123", tipo "onnx".
  return ponto > 0 ? { name: arquivo.slice(0, ponto), type: arquivo.slice(ponto + 1).toLowerCase() } : { name: arquivo, type: "" };
}

export class Asset {
  name: string;
  type: string;
  hash: string | null;
  uri: string;
  localUri: string | null = null;
  width: number | null;
  height: number | null;
  downloaded = false;
  private baixando: Promise<void> | null = null;

  constructor({ uri, name, type, hash = null, width, height }: AssetMetadata) {
    const derivado = nomeEtipo(uri);
    this.uri = uri;
    this.name = name ?? derivado.name;
    this.type = type ?? derivado.type;
    this.hash = hash;
    this.width = width ?? null;
    this.height = height ?? null;
    if (uri.startsWith("data:") || uri.startsWith("blob:") || uri.startsWith("file:")) {
      this.localUri = uri;
      this.downloaded = true;
    }
  }

  /** Aceita a URL do asset (import do Vite), `{uri}` ou um `Asset`. */
  static fromModule(virtualAssetModule: string | number | { uri: string } | Asset): Asset {
    if (virtualAssetModule instanceof Asset) return virtualAssetModule;
    let uri: string;
    if (typeof virtualAssetModule === "string") uri = virtualAssetModule;
    else if (typeof virtualAssetModule === "object" && virtualAssetModule && "uri" in virtualAssetModule) uri = virtualAssetModule.uri;
    else throw new Error(`Asset.fromModule: módulo inválido no web (${String(virtualAssetModule)}). Use import da URL.`);
    const existente = cachePorUri.get(uri);
    if (existente) return existente;
    const a = new Asset({ uri });
    cachePorUri.set(uri, a);
    return a;
  }

  static fromURI(uri: string): Asset {
    return Asset.fromModule(uri);
  }

  static fromMetadata(meta: AssetMetadata): Asset {
    const existente = cachePorUri.get(meta.uri);
    if (existente) return existente;
    const a = new Asset(meta);
    cachePorUri.set(meta.uri, a);
    return a;
  }

  /** Baixa vários (mesmo formato do expo-asset). */
  static async loadAsync(modulos: string | number | (string | number)[]): Promise<Asset[]> {
    const lista = Array.isArray(modulos) ? modulos : [modulos];
    return Promise.all(lista.map((m) => Asset.fromModule(m as string).downloadAsync()));
  }

  async downloadAsync(): Promise<this> {
    if (this.downloaded) return this;
    if (!this.baixando) {
      this.baixando = (async () => {
        try {
          const r = await fetch(this.uri, { cache: "force-cache" });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          // Consome o corpo para o cache (HTTP/SW) ficar completo.
          await r.arrayBuffer();
          this.localUri = this.uri;
          this.downloaded = true;
        } catch (e) {
          this.baixando = null;
          throw new Error(`Falha ao baixar o asset ${this.uri}: ${(e as Error)?.message ?? e}`);
        }
      })();
    }
    await this.baixando;
    return this;
  }
}

/** Hook do expo-asset: `[assets, erro]`. */

export function useAssets(modulos: string | number | (string | number)[]): [Asset[] | undefined, Error | undefined] {
  const [assets, setAssets] = useState<Asset[] | undefined>(undefined);
  const [erro, setErro] = useState<Error | undefined>(undefined);
  const chave = JSON.stringify(Array.isArray(modulos) ? modulos : [modulos]);
  useEffect(() => {
    let vivo = true;
    Asset.loadAsync(JSON.parse(chave) as (string | number)[])
      .then((a) => vivo && setAssets(a))
      .catch((e) => vivo && setErro(e instanceof Error ? e : new Error(String(e))));
    return () => {
      vivo = false;
    };
  }, [chave]);
  return [assets, erro];
}
